import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { RidAssessmentToast } from '../../components/rid-assessment-toast/rid-assessment-toast';
import { RidAudioButton } from '../../components/rid-audio-button/rid-audio-button';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { AudioEffectPlayer } from '../../utils/audio.utils';

@Component({
  selector: 'rid-dictation-sentences',
  imports: [
    RidAssessmentToast,
    RidAudioButton,
    RidBottomButton,
    RidMechanicBackground,
    RidMechanicContent,
    RidMechanicTitle,
    RidMechanicWrapper,
    RidOverlayFeedback,
    RidTopBar,
    RidSfxDirective,
  ],
  templateUrl: './rid-dictation-sentences.html',
  host: {
    class: 'block',
  },
})
export class RidDictationSentences {
  sentences = input.required<string[]>();

  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  audioButton = viewChild.required<RidAudioButton>('audioButton');
  index = signal(0);
  state = signal<'AUDIO' | 'WRITING' | 'FEEDBACK'>('AUDIO');
  requiresWrongWordSelection = signal(false);
  selectedWordIndices = signal<number[]>([]);
  selectedWordIndicesSet = computed(() => new Set(this.selectedWordIndices()));

  sentence = computed(() => this.sentences()[this.index()]);
  sentenceKey = computed(() => this.sentence());
  sentenceWords = computed(() => this.sentence().trim().split(/\s+/).filter(Boolean));
  title = computed(() => {
    switch (this.state()) {
      case 'WRITING':
        return 'Schrijf de zin in je schrift';
      case 'FEEDBACK':
        return this.requiresWrongWordSelection() ? 'Klik op de woorden die fout waren' : undefined;
      default:
        return undefined;
    }
  });

  finished = output<void>();

  async start() {
    if (this.state() !== 'AUDIO') return;

    await this.audioButton().play();

    if (this.state() === 'AUDIO') {
      this.state.set('WRITING');
    }
  }

  async markCorrect() {
    AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();

    this.next();
  }

  async markIncorrect() {
    AudioEffectPlayer.play('INCORRECT');
    this.requiresWrongWordSelection.set(true);
    this.selectedWordIndices.set([]);
    this.overlayFeedback().showIncorrect();
  }

  toggleWord(index: number, selected: boolean) {
    if (!this.requiresWrongWordSelection()) return;

    if (selected) {
      this.selectedWordIndices.update((indices) => [...indices, index]);
      return;
    }

    this.selectedWordIndices.update((indices) => indices.filter((currentIndex) => currentIndex !== index));
  }

  next() {
    this.requiresWrongWordSelection.set(false);
    this.selectedWordIndices.set([]);

    const nextIndex = this.index() + 1;
    if (nextIndex < this.sentences().length) {
      this.index.set(nextIndex);
      this.state.set('AUDIO');
      return;
    }

    this.finished.emit();
  }

  showAssessmentToast() {
    return this.state() === 'FEEDBACK' && !this.requiresWrongWordSelection();
  }

  showContinueButton() {
    return this.state() === 'FEEDBACK' && this.requiresWrongWordSelection();
  }

  isWordSelected(index: number) {
    return this.selectedWordIndicesSet().has(index);
  }

  isWordClickable() {
    return this.state() === 'FEEDBACK' && this.requiresWrongWordSelection();
  }

  onDone() {
    this.state.set('FEEDBACK');
  }
}
